import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import type { SkinConcern } from './types';
import type { UserSkinProfile } from '../../types';
import ApiService from '../../services/api';
import { isConcernListed } from '../../services/excludedConcerns';
import { mapSessionsToProfiles } from './utils';
import { MoreUserDetailsView } from './index';

export default function CustomerDataTable() {
  const {
    sessionId,
    profileId,
    token,
    userId,
    role,
    userProfilesCache,
    fetchUserProfiles,
    setAnalysisSessions,
    setOutputScore,
    setProfileImageUrl,
    getTranslation,
    isRTL,
    setCurrentView,
  } = useAppContext();

  const [profileData, setProfileData] = useState<UserSkinProfile[]>([]);
  const [userProfileData, setUserProfileData] = useState<{ first_name?: string; last_name?: string; phone_number?: string }>({});
  const [isLoading, setIsLoading] = useState(true);

  const hasFetchedUserProfilesRef = useRef(false);
  const isCustomerRole = role === 2;

  // Fetch user profile info for distributor name box
  useEffect(() => {
    if (!isCustomerRole) return;
    hasFetchedUserProfilesRef.current = false;
  }, [token, userId, sessionId, isCustomerRole]);

  useEffect(() => {
    if (!isCustomerRole || !token || !userId || hasFetchedUserProfilesRef.current) {
      return;
    }
    if (userProfilesCache.isLoading) return;

    const run = async () => {
      try {
        hasFetchedUserProfilesRef.current = true;
        const api = new ApiService(sessionId, token);
        await fetchUserProfiles(api, 2);
      } catch (e) {
        console.error('Failed to fetch user profiles:', e);
        hasFetchedUserProfilesRef.current = false;
      }
    };

    run();
  }, [token, userId, sessionId, fetchUserProfiles, userProfilesCache.isLoading, isCustomerRole]);

  useEffect(() => {
    if (!isCustomerRole) return;
    const cachedData = userProfilesCache.data as any;
    if (!cachedData) return;

    let user: any = null;
    if (Array.isArray(cachedData) && cachedData.length > 0) {
      const userProfile = cachedData[0];
      user = 'user' in userProfile ? userProfile.user : userProfile;
    } else if (typeof cachedData === 'object' && cachedData !== null) {
      if ('user' in cachedData && cachedData.user) {
        user = cachedData.user.user || cachedData.user;
      } else if ('first_name' in cachedData || 'last_name' in cachedData) {
        user = cachedData;
      }
    }

    if (user) {
      setUserProfileData({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        phone_number: user.phone_number || '',
      });
    }
  }, [userProfilesCache.data, isCustomerRole]);

  // Fetch sessions for customer
  useEffect(() => {
    if (!isCustomerRole || !token || !userId) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    const run = async () => {
      setIsLoading(true);

      try {
        const api = new ApiService(sessionId, token);
        const response = await api.getAllUserSessions(userId);
        if (abortController.signal.aborted) return;

        const session = response.data?.sessions;
        if (session) {
          const sessionsArray = [session];
          setAnalysisSessions(sessionsArray);

          const activeSession = sessionsArray.find((s) => s._id === profileId) || sessionsArray[0];
          if (activeSession?.output_score?.length) {
            setOutputScore(activeSession.output_score);
            if (activeSession.input_image_url) {
              setProfileImageUrl(activeSession.input_image_url);
            }
          }

          setProfileData(mapSessionsToProfiles(sessionsArray));
        }
      } catch (e) {
        if (!abortController.signal.aborted) {
          console.error('Error fetching customer sessions:', e);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    run();
    return () => abortController.abort();
  }, [profileId, token, userId, sessionId, setAnalysisSessions, setOutputScore, setProfileImageUrl, isCustomerRole]);

  const moreProfileData = profileData;
  const filteredProfileData = moreProfileData;
  const selectedProfile = moreProfileData[0];

  // Prefer selected profile's output_score so score/skin UI never shows another user's data.
  // IMPORTANT: Avoid falling back to global outputScore; it can contain a previous tab/user value.
  const scoreSource = selectedProfile?.output_score?.length ? selectedProfile.output_score : null;

  const { skinOutput, score } = useMemo(() => {
    const empty = { skinOutput: [] as SkinConcern[], score: 0 };
    if (!scoreSource || scoreSource.length === 0) return empty;

    const skin_health = scoreSource.find((item) => item.concern === 'skin_health');
    const sorted = scoreSource
      .filter((item) => isConcernListed(item.concern))
      .sort((a, b) => a.score - b.score);

    return { skinOutput: sorted, score: skin_health ? skin_health.score : 0 };
  }, [scoreSource]);

  const handleNavigateBack = () => setCurrentView('Welcome');

  // UI-owned handlers are still provided by view via props; we pass no-op placeholders because view re-implements them.
  // To keep compatibility with existing UI, we pass the same handlers as before by reusing inline implementations:
  const [selectedAnnotationImage, setSelectedAnnotationImage] = useState<string | null>(null);
  const [selectedConcernId, setSelectedConcernId] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [fullImageModalOpen, setFullImageModalOpen] = useState(false);

  const handleCopyEmail = async () => {
    if (!selectedProfile?.email) return;
    await navigator.clipboard.writeText(selectedProfile.email);
  };

  const handleConcernClick = (issue: SkinConcern) => {
    const annotationImageUrl = selectedProfile?.annotations?.[issue.concern];
    if (!annotationImageUrl) return;
    setImageLoading(true);
    setIsImageLoaded(false);
    setSelectedConcernId(issue.concern);
    const img = new Image();
    img.onload = () => {
      setSelectedAnnotationImage(annotationImageUrl);
      setIsImageLoaded(true);
      setImageLoading(false);
    };
    img.onerror = () => {
      setSelectedAnnotationImage(annotationImageUrl);
      setIsImageLoaded(true);
      setImageLoading(false);
    };
    img.src = annotationImageUrl;
  };

  const handleCloseAnnotationModal = () => {
    setSelectedAnnotationImage(null);
    setSelectedConcernId(null);
    setImageLoading(true);
    setIsImageLoaded(false);
  };

  return (
    <MoreUserDetailsView
      role={role}
      profileId={profileId}
      isRTL={isRTL}
      getTranslation={getTranslation}
      userProfilesCache={userProfilesCache}
      skinOutput={skinOutput}
      score={score}
      selectedAnnotationImage={selectedAnnotationImage}
      selectedConcernId={selectedConcernId}
      imageLoading={imageLoading}
      isImageLoaded={isImageLoaded}
      fullImageModalOpen={fullImageModalOpen}
      moreProfileData={moreProfileData}
      userProfileData={userProfileData}
      filteredProfileData={filteredProfileData}
      selectedProfile={selectedProfile}
      isLoadingDetails={isLoading}
      setFullImageModalOpen={setFullImageModalOpen}
      handleCopyEmail={handleCopyEmail}
      handleConcernClick={handleConcernClick}
      handleCloseAnnotationModal={handleCloseAnnotationModal}
      handleNavigateBack={handleNavigateBack}
    />
  );
}

